import { TriggerCreate } from "@dots-projects/types";
import { v4 as uuidv4 } from "uuid";
import gif from "./support-ticket-gif.gif";

export const suportTicketTemplate = (
  workspace: string
): {
  trigger: TriggerCreate;
  gif: any;
  title: string;
  description: string;
} => {
  return {
    title: "Support Ticket (+ private channels)",
    description:
      "<p>Organize your members' help requests through a support ticket bot! Use this template to create a customized support ticket bot that will help you triage questions. Use this to set up an initial set of self-serve help content, and give the option to open a private support channel if a member still can't find help in your docs.</p><p className=\"mt-10\">Explore the logic of this template in the editor view on the left.</p>",
    gif,
    trigger: {
      name: "Support Ticket (+ private channels)",
      type: "button_click",
      platform: "discord",
      communityId: localStorage.getItem("guild-id") || "",
      authorUserId: localStorage.getItem("discord-id") || "",
      workspace,
      isDraft: true,
      configs: {
        messages: [
          {
            id: uuidv4(),
            buttonIds: [uuidv4()],
          },
        ],
        responseOnError: "Something went wrong, please try again!",
        $$editorButtonClickContent: {
          message: "Click below if you have any support related questions!",
          channelId: "$channelId",
          buttonText: "Get help",
          buttonEmoji: "🆘",
          htmlMessage:
            "<p>Click below if you have any support related questions!</p>",
        },
      },
      steps: [
        {
          id: "send_survey-0-6h3fXJ",
          name: "Ask triage question",
          type: "send_survey",
          configs: {
            type: "buttons",
            message:
              "Hey {{user.username}} ! Thanks for reaching out! What type of issue is this?",
            options: [
              {
                id: "africa",
                emoji: "📱",
                label: "App not working",
              },
              {
                id: "na",
                emoji: "👩‍💻",
                label: "Need help with setup",
              },
              {
                id: "something-else",
                emoji: "⁉️",
                label: "Something else",
              },
            ],
            channelId: "{{interaction.channelId}}",
            ephemeral: true,
            $$editorHtmlMessage:
              '<p>Hey <span class="mention" data-index="0" data-denotation-char="{{" data-id="user.username" data-value="username}}">﻿<span contenteditable="false"><span class="ql-mention-denotation-char">{{</span>username}}</span>﻿</span> ! Thanks for reaching out! What type of issue is this?</p>',
          },
          isEntry: true,
          isGoal: false,
          transitions: [
            {
              targetStepId: "send_message-0-oZxmaB",
              when: {
                userResponse: {
                  type: "button",
                  userId: "{{user.id}}",
                  buttonId: "africa",
                },
              },
            },
            {
              targetStepId: "send_message-0-DwkbfQ",
              when: {
                userResponse: {
                  type: "button",
                  userId: "{{user.id}}",
                  buttonId: "na",
                },
              },
            },
            {
              targetStepId: "create_channel-0-0iNQy4",
              when: {
                userResponse: {
                  type: "button",
                  userId: "{{user.id}}",
                  buttonId: "something-else",
                },
              },
            },
          ],
        },
        {
          id: "send_message-0-oZxmaB",
          name: "Initial message",
          type: "send_message",
          configs: {
            message:
              "okay! Can you follow instructions here first to see if it resolves?",
            channelId: "{{interaction.channelId}}",
            ephemeral: true,
            removeUrlEmbeds: false,
            $$editorHtmlMessage:
              "<p>okay! Can you follow instructions here first to see if it resolves?</p>",
          },
          isEntry: false,
          isGoal: false,
          transitions: [
            {
              targetStepId: "send_survey-0-0F7X3c",
            },
          ],
        },
        {
          id: "send_message-0-DwkbfQ",
          name: "Send a message",
          type: "send_message",
          configs: {
            message:
              "We have information here on setting up with FAQs. Please check it out first!",
            channelId: "{{interaction.channelId}}",
            ephemeral: true,
            removeUrlEmbeds: false,
            $$editorHtmlMessage:
              "<p>We have information here on setting up with FAQs. Please check it out first!</p>",
          },
          isEntry: false,
          isGoal: false,
          transitions: [
            {
              targetStepId: "send_survey-0-0F7X3c",
            },
          ],
        },
        {
          type: "create_channel",
          name: "Create a private support channel channel",
          id: "create_channel-0-0iNQy4",
          configs: {
            userIds: ["{{user.id}}"],
            isPrivate: true,
            channelName: "support-{{user.username}}",
            channelType: "onboarding",
          },
          isEntry: false,
          isGoal: false,
          transitions: [
            {
              targetStepId: "send_message-1-aRVOv8",
            },
          ],
        },
        {
          id: "send_message-1-aRVOv8",
          name: "Send a message",
          type: "send_message",
          configs: {
            message:
              "Hi <@{{user.id}}> ! Can you please share a bit more about your issue so one of our team members can help?",
            channelId: "{{create_channel-0-0iNQy4.channelId}}",
            ephemeral: false,
            removeUrlEmbeds: false,
            $$editorHtmlMessage:
              '<p>Hi <span class="mention" data-index="1" data-denotation-char="{{" data-id="<@user.id>" data-value="@username}}">﻿<span contenteditable="false"><span class="ql-mention-denotation-char">{{</span>@username}}</span>﻿</span> ! Can you please share a bit more about your issue so one of our team members can help?</p>',
          },
          isEntry: false,
          isGoal: false,
          transitions: [],
        },
        {
          id: "send_survey-0-0F7X3c",
          name: "Confirm if they still need help",
          type: "send_survey",
          configs: {
            type: "buttons",
            message:
              "After looking at the info, do you think you still need more help?",
            options: [
              {
                id: "africa",
                emoji: "✅",
                label: "Yes",
              },
              {
                id: "na",
                emoji: "😇",
                label: "Nope, I am all set",
              },
            ],
            channelId: "{{interaction.channelId}}",
            ephemeral: true,
            $$editorHtmlMessage:
              "<p>After looking at the info, do you think you still need more help?</p>",
          },
          isEntry: false,
          isGoal: false,
          transitions: [
            {
              targetStepId: "create_channel-0-0iNQy4",
              when: {
                userResponse: {
                  type: "button",
                  userId: "{{user.id}}",
                  buttonId: "africa",
                },
              },
            },
            {
              targetStepId: "send_message-0-rebh5F",
              when: {
                userResponse: {
                  type: "button",
                  userId: "{{user.id}}",
                  buttonId: "na",
                },
              },
            },
          ],
        },
        {
          id: "send_message-0-rebh5F",
          name: "Send a message",
          type: "send_message",
          configs: {
            message:
              "Awesome! Let us know here if you have any more questions!",
            channelId: "{{interaction.channelId}}",
            ephemeral: true,
            removeUrlEmbeds: false,
            $$editorHtmlMessage:
              "<p>Awesome! Let us know here if you have any more questions! </p>",
          },
          isEntry: false,
          isGoal: false,
          transitions: [],
        },
      ],
    },
  };
};
